import { get, post, put } from "aws-amplify/api";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import HeadOfficePageLeaseList from "containers/headoffice/page/HeadOfficePageLeaseList";
import useAutoSave from "hooks/useAutoSave";
import HeadOfficePageGeneralInfo from "containers/headoffice/page/HeadOfficePageGeneralInfo";
import { validateHeadOfficeRecord } from "containers/headoffice/page/headOfficeValidation";
import RecordHistory from "containers/common/RecordHistory";
import { Alert, Button, Card, Col, Form, Modal, Nav, Row, Tab, Tabs } from "react-bootstrap";
import { PacmanLoader } from 'react-spinners';
import { useLocation, useParams, useNavigate, Link } from "react-router-dom";
import { createElementIdFactory } from "utils/elementIds";
import "containers/common/List.css";
import "containers/common/Table.css";
import 'containers/common/Detail.css';

const initialHeadOffice = {
    led_head_office_id: "new",
    tenant_name: "",
    address_1: "",
    address_2: "",
    suburb: "",
    postcode: "",
    state: "",
    phone: "",
    email: "",
    website: "",
};

function HeadOfficePageDetail({
    isAuthenticated,
    user, }) {

    const location = useLocation();
    const params = useParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(params.id !== 'new');
    const ledheadofficeid = useRef(params.id);
    const [modified, setModified] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [suburbOptions, setSuburbOptions] = useState([]);
    const [headOffice, setHeadOffice] = useState(initialHeadOffice);
    const [headOffLeases, setHeadOffLeases] = useState([]);
    const [selectedSuburb, setSelectedSuburb] = useState(null);
    const createId = useMemo(() => createElementIdFactory("head-office-detail"), []);

    const {
        led_head_office_id,
        tenant_name,
        state,
        telephone,
        created_date,
        created_by,
        last_edited_date,
        last_edited_by
    } = headOffice;

    const leaseid = location.state?.leaseid;
    const propertyid = location.state?.propertyid;

  // --- API Functions ---
    const getHeadOffice = useCallback(async () => {
        if (params.id === "new") {
            return { led_head_office_id: "new", tenant_name: "" };
        }

        const { body } = await get({
            apiName: "colin",
            path: `/headoffices/${params.id}`,
        }).response;
        return await body.json();
    }, [params.id]);

    const getSuburbs = useCallback(async () => {
        try {
            const response = await get({
                apiName: "colin",
                path: `/suburbs`,
            }).response;

            return await response.body.json();

        } catch (error) {
            console.error("Error fetching suburbs:", error);
            setErrorMessage("Error fetching suburbs: " + error + ". Check console for details.");
            setShowAlert(true);
            return [];
        }
    }, []);

    const getHeadOfficeLeases = useCallback(async (id, filter = {}) => {
        const actualId = typeof id === 'object' ? id.current : id;

        if (!actualId || actualId === "new") {
            return [];
        }

        const path = `/headoffices/${actualId}/headofficeleases?ledheadofficeid=${actualId}`;

        try {
            const response = await get({
                apiName: "colin",
                path: path,
            }).response;

            return await response.body.json();

        } catch (e) {
            console.error("Error fetching leases:", e);
            return [];
        }
    }, []);

    // --- API Save Function (Callback) ---
    function validateRecord(record) {
        return validateHeadOfficeRecord(record);
    }

    const saveHeadOfficeAPI = useCallback(async (record) => {
        const isNew = record.led_head_office_id === "new" || !record.led_head_office_id;

        const payload = Object.fromEntries(
            Object.entries(record).filter(([key, v]) =>
                v !== null &&
                v !== "" &&
                v !== "new" &&
                v !== "null" &&
                key !== "led_head_office_id"
            )
        );

        payload.last_edited_by = user;
        if (isNew) payload.created_by = user;

        const apiCall = isNew ? post : put;
        const path = isNew ? "/headoffices/new" : `/headoffices/${record.led_head_office_id}`;

        try {
            const response = await apiCall({
                apiName: "colin",
                path,
                options: { body: payload }
            }).response;
            return await response.body.json();
        } catch (error) {
            return { result: false, error_message: "Save failed." };
        }
    }, [user]);

    // --- Auto-Save Handler (Callback) ---
    const handleAutoSave = useCallback(async (localData) => {
        setModified(false);

        const validation = validateRecord(localData);
        if (!validation[0]) {
            if (localData.led_head_office_id === 'new' && !localData.tenant_name) return;
            setErrorMessage(validation[1]);
            setShowAlert(true);
            return;
        }

        try {
            const result = await saveHeadOfficeAPI(localData);

            if (result.result && result.id) {
                const newIdString = result.id.toString();

                ledheadofficeid.current = newIdString;

                setHeadOffice(prev => ({
                    ...prev,
                    led_head_office_id: result.id
                }));

                if (params.id === 'new') {
                    navigate(`/headoffices/${newIdString}`, { replace: true });
                }
            }
        } catch (err) {
            console.error("Auto-save error", err);
        }
    }, [navigate, params.id, saveHeadOfficeAPI]);

    // --- Effect Hook  ---
    useEffect(() => {
        if (!isAuthenticated) return;

        const currentId = headOffice.led_head_office_id?.toString();

        if (currentId !== "new" && currentId === params.id) {
            setIsLoading(false);
            return;
        }

        ledheadofficeid.current = params.id;
        let isMounted = true;

        const loadData = async () => {
            setIsLoading(true);

            const idToFetch = params.id;
            const filter = {
                led_head_office_id: idToFetch
            };

            try {
                const [localSuburbOptions, localHeadOffice, localHeadOfficeLeases] = await Promise.all([
                    getSuburbs(),
                    getHeadOffice(),
                    getHeadOfficeLeases(idToFetch, filter)
                ]);

                if (!isMounted) return;

                setSuburbOptions(localSuburbOptions || []);
                setHeadOffice(localHeadOffice);
                setHeadOffLeases(localHeadOfficeLeases);

                if (localHeadOffice.suburb && localSuburbOptions?.length > 0) {
                    const initialSelected = localSuburbOptions.find(opt => opt.value === localHeadOffice.suburb);
                    setSelectedSuburb(initialSelected || { value: localHeadOffice.suburb, label: localHeadOffice.suburb });
                }

            } catch (e) {
                if (isMounted) setShowAlert(true);
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        loadData();
        return () => { isMounted = false; };

    }, [isAuthenticated, params.id, getHeadOffice, getSuburbs, getHeadOfficeLeases, headOffice.led_head_office_id]);

    // --- Input Change Handlers ---
    const handleChange = (event) => {
        const { id, name, value } = event.target;
        const fieldName = name || id;

        setHeadOffice(prev => ({
            ...prev,
            [fieldName]: value,
        }));
        setModified(true);
    };

    const handleSelectChange = (selectedOption, actionMeta) => {
        const { name } = actionMeta;

        setHeadOffice(prev => {
            let cleanOption = null;
            if (selectedOption) {
                const cleanLabel = selectedOption.label.replace(/\s\d{4}(?=\))/, "");

                cleanOption = { ...selectedOption, label: cleanLabel };
            }

            const newState = {
                ...prev,
                [name]: selectedOption ? selectedOption.value : null,
                [`selected_${name}`]: cleanOption,
            };

            if (name === 'suburb' && selectedOption) {
                if (selectedOption.state) newState.state = selectedOption.state;
                if (selectedOption.postcode) newState.postcode = selectedOption.postcode;
            }

            return newState;
        });

        setModified(true);
    };

    const updateHeadOfficeLeaseTableState = useCallback((rowIndex, columnId, value) => {
        setHeadOffLeases(prev => {
            const next = [...prev];
            if (columnId === "") {
                next.splice(rowIndex, 0, value);
                return next;
            }
            if (!next[rowIndex]) return prev;
            next[rowIndex] = { ...next[rowIndex], [columnId]: value, modified: true };
            return next;
        });
    }, []);

    const saveHeadOfficeLeaseRecord = useCallback(async (dataSet, record) => {
        if (dataSet !== "headOfficeLeases") {
            return { result: false, error_message: `Unknown data set: ${dataSet}` };
        }

        if (!String(record?.tenant_name || "").trim()) {
            return { result: false, error_message: "Enter a tenant name from a lease" };
        }

        const headOfficeId = record.led_head_office_id || ledheadofficeid.current;
        if (!headOfficeId || headOfficeId === "new") {
            return { result: false, error_message: "Save the Head Office before adding leases." };
        }

        const isNew = record.new_record === true || !record.led_head_office_lease_id;
        const path = isNew
            ? `/headoffices/${headOfficeId}/headofficeleases/new`
            : `/headoffices/${headOfficeId}/headofficeleases/${record.led_head_office_lease_id}`;
        const request = isNew ? post : put;

        try {
            const response = await request({
                apiName: "colin",
                path,
                options: { body: { ...record, led_head_office_id: headOfficeId, last_edited_by: user } },
            }).response;
            const result = await response.body.json();

            if (result?.result === false) return result;

            setHeadOffLeases(prev => prev.map(currentRecord => {
                const isMatchingRecord = record._client_id
                    ? currentRecord._client_id === record._client_id
                    : String(currentRecord.led_head_office_lease_id) === String(record.led_head_office_lease_id);
                if (!isMatchingRecord) return currentRecord;

                return {
                    ...currentRecord,
                    ...record,
                    ...result,
                    led_head_office_lease_id: result?.led_head_office_lease_id ?? result?.id ?? record.led_head_office_lease_id,
                    new_record: false,
                    modified: false,
                };
            }));

            return result;
        } catch (error) {
            return { result: false, error_message: error?.message || "Failed to save Head Office lease." };
        }
    }, [user]);

    const deleteHeadOfficeLeaseRecord = useCallback(async (dataSet, record) => {
        if (dataSet !== "headOfficeLeases") {
            return { result: false, error_message: `Unknown data set: ${dataSet}` };
        }

        if (!record?.led_head_office_lease_id) {
            setHeadOffLeases(prev => prev.filter(item => item._client_id !== record?._client_id));
            return { result: true };
        }

        const result = await saveHeadOfficeLeaseRecord(dataSet, {
            ...record,
            archived_date: new Date().toISOString(),
            modified: true,
        });
        if (result?.result !== false) {
            setHeadOffLeases(prev => prev.filter(item => String(item.led_head_office_lease_id) !== String(record.led_head_office_lease_id)));
        }
        return result;
    }, [saveHeadOfficeLeaseRecord]);

    useAutoSave(headOffice, modified, handleAutoSave, isLoading);

    // --- Render Functions ---
    function renderForm() {
        return (
            <div className="HeadOfficeForm">
                <Form>
                    <Row className="mt-3">
                        <Col sm={12} md={12} lg={12}>
                            <h6>Head Office - {tenant_name || 'New Record'} | {telephone} | {state}</h6>
                        </Col>
                        <Col>
                            <Nav className="justify-content-end">
                                {leaseid &&
                                    <Nav.Item>
                                        <Link
                                            to={{
                                                pathname: `/properties/${propertyid}/leases/${leaseid}`,
                                                state: { data: (location.state?.data || []) }
                                            }}
                                        >
                                            <Button id={createId("back-to-lease-btn")} type="button" variant="primary" size="sm">Back to Lease {leaseid}</Button>
                                        </Link>{" "}
                                    </Nav.Item>
                                }
                            </Nav>
                        </Col>
                    </Row>

                    <Row id={createId("detail")}>
                        <Col>
                            <Modal id={createId("error-modal")} show={showAlert} onHide={() => setShowAlert(false)} centered >
                                <Modal.Header id={createId("error-modal-header")} closeButton> <Modal.Title>Uh Oh!</Modal.Title></Modal.Header>
                                <Modal.Body id={createId("error-modal-body")} > <Alert variant="danger">{errorMessage}</Alert> </Modal.Body>
                            </Modal>

                            {showAlert && !led_head_office_id &&
                                <Alert variant="danger">{errorMessage}</Alert>
                            }

                            <Tabs defaultActiveKey={0} id={createId("tabset")} className="justify-content-start">
                                <Tab eventKey={0} title="Head Office Detail">
                                    <HeadOfficePageGeneralInfo
                                        headOffice={headOffice}
                                        handleChange={handleChange}
                                        selectedSuburb={selectedSuburb}
                                        suburbOptions={suburbOptions}
                                        handleSelectChange={handleSelectChange}
                                    />
                                </Tab>

                                <Tab eventKey={1} title="Leases">
                                    <Card className="entity-detail-tab-card">
                                        <Card.Body>
                                            <HeadOfficePageLeaseList
                                                isAuthenticated={isAuthenticated}
                                                data={headOffLeases}
                                                user={user}
                                                ledheadofficeid={led_head_office_id}
                                                updateTableState={updateHeadOfficeLeaseTableState}
                                                saveRecord={saveHeadOfficeLeaseRecord}
                                                deleteRecord={deleteHeadOfficeLeaseRecord}
                                            />
                                        </Card.Body>
                                    </Card>
                                </Tab>

                                <Tab eventKey={2} title="Record History">
                                    <RecordHistory
                                        created_date={created_date}
                                        created_by={created_by}
                                        last_edited_date={last_edited_date}
                                        last_edited_by={last_edited_by}
                                    />
                                </Tab>
                            </Tabs>
                        </Col>
                    </Row>
                </Form>
            </div>
        )
    }

    function renderComponent() {
        return (
            <div className="HeadOffice">
                <Row className="show-Container">
                    <Col sm={12} md={12} lg={12}>
                        {isLoading && (
                            <div className="outer-div">
                                <div className="sweet-loading inner-div">
                                    <PacmanLoader
                                        cssOverride={{ display: "block", margin: "0 auto" }}
                                        size={20}
                                        color={"#e03c31"}
                                        loading={isLoading}
                                    />
                                </div>
                            </div>
                        )}

                        {headOffice && isAuthenticated && !isLoading
                            ? renderForm()
                            : null
                        }
                    </Col>
                </Row>
            </div>
        );
    }
    return (
        <div className="HeadOfficeDetailxx">
            {isAuthenticated ? renderComponent() : null}
        </div>
    );
}

export default HeadOfficePageDetail;